import {
	PAGINATION_LIMIT_PER_PAGE,
	usePagination,
} from '@dealstack/frontend/shared/other/react/hooks/usePagination';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { updateURLParam, UpdateURLParam } from '@dealstack/frontend/shared/helper/updateUrlParams';
import { KYC_PAGINATION_PARAMS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/constants';

interface UsePaginationWithSearchOptions {
	defaultOffset?: number;
	defaultLimit?: PAGINATION_LIMIT_PER_PAGE;
	initialTotalNumberOfItems?: number;
}

export const usePaginationWithSearch = (options: UsePaginationWithSearchOptions) => {
	const {
		defaultOffset = 0,
		defaultLimit = PAGINATION_LIMIT_PER_PAGE.x25,
		initialTotalNumberOfItems = 10,
	} = options;

	const [searchParams, setSearchParams] = useSearchParams();

	// initialize pagination from URL search params if they exist
	const offsetParam = searchParams.get(KYC_PAGINATION_PARAMS.OFFSET_PARAM_KEY);
	const limitParam = searchParams.get(KYC_PAGINATION_PARAMS.LIMIT_PARAM_KEY);
	const searchQueryParam = searchParams.get(KYC_PAGINATION_PARAMS.SEARCH_QUERY_PARAM_KEY);
	const initialOffset = offsetParam ? parseInt(offsetParam, 10) : defaultOffset;
	const initialLimit = limitParam
		? (parseInt(limitParam, 10) as PAGINATION_LIMIT_PER_PAGE)
		: defaultLimit;
	const initialSearchQuery = searchQueryParam || undefined;

	const pagination = usePagination({
		offset: initialOffset,
		limit: initialLimit,
		totalNumberOfItems: initialTotalNumberOfItems,
	});

	const [searchQuery, setSearchQuery] = useState<string | undefined>(initialSearchQuery);
	const onSearchQueryChange = useCallback(
		(query?: string) => {
			setSearchQuery(query);
			pagination.resetOffset();
		},
		[pagination]
	);

	// sync pagination state to URL search params
	useEffect(() => {
		const newParams = new URLSearchParams(searchParams);
		const updateParams: UpdateURLParam[] = [
			{
				key: KYC_PAGINATION_PARAMS.OFFSET_PARAM_KEY,
				condition: true,
				value: pagination.state.offset.toString(),
				newParams,
			},
			{
				key: KYC_PAGINATION_PARAMS.LIMIT_PARAM_KEY,
				condition: true,
				value: pagination.state.limit.toString(),
				newParams,
			},
			{
				key: KYC_PAGINATION_PARAMS.SEARCH_QUERY_PARAM_KEY,
				condition: !!searchQuery,
				value: searchQuery ?? '',
				newParams,
			},
		];
		updateParams.forEach((updateParam) => {
			updateURLParam(updateParam);
		});

		const oldParams = searchParams.toString();
		const newParamsString = newParams.toString();

		if (oldParams !== newParamsString) {
			setSearchParams(newParams, { replace: true });
		}
		// search params are set in this effect, so we don't want to add it as a dependency
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.state.offset, pagination.state.limit, searchQuery, setSearchParams]);

	return {
		pagination,
		searchQuery,
		onSearchQueryChange,
	};
};
